import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DonateModal from '../components/DonateModal';
import Preloader from '../components/Preloader';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AlertBar from '../components/AlertBar';

const Index = () => {
  const [showAlert, setShowAlert] = useState(true);
  const [donateModalOpen, setDonateModalOpen] = useState(false);
  const [donateAmountSelected, setDonateAmountSelected] = useState(10);
  const [customDonateAmount, setCustomDonateAmount] = useState('');
  const navigate = useNavigate();
  const alert = {
    type: 'info',
    message: '📢 Limited Time: Help us reach our 2026 goals! Donate now and make a direct impact on our programs.'
  };

  useEffect(() => {
    // Re-initialize progress bars and other scripts after component mounts
    const initProgressBars = () => {
      if (window.progressBarsInitialized) {
        // Reset and re-initialize
        window.progressBarsInitialized = false;
      }
      
      // Re-run progressbar script if it exists
      const progressBars = document.querySelectorAll('.ul-progressbar:not([data-initialized])');
      if (progressBars.length > 0 && typeof window !== 'undefined') {
        progressBars.forEach(progressBar => {
          if (progressBar.hasAttribute('data-initialized')) return;
          
          progressBar.setAttribute('data-initialized', 'true');
          const targetValue = parseInt(progressBar.getAttribute('data-ul-progress-value'), 10);
          const progressLabel = progressBar.querySelector(".ul-progress-label");
          let currentValue = 0;

          const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                const updateProgressBar = () => {
                  if (currentValue < targetValue) {
                    currentValue++;
                    progressBar.style.width = `${currentValue}%`;
                    if (progressLabel) {
                      progressLabel.textContent = `${currentValue}%`;
                    }
                    requestAnimationFrame(updateProgressBar);
                  }
                };

                updateProgressBar();
                observer.unobserve(progressBar);
              }
            });
          });

          observer.observe(progressBar);
        });
      }
    };

    // Initialize sliders and animations after scripts are loaded
    const initSliders = () => {
      // Initialize WOW.js animations (only once)
      if (window.WOW && !window.__wowInitialized) {
        window.__wowInitialized = true;
        new window.WOW().init();
      }

      // Initialize Swiper sliders
      if (window.Swiper) {
        // Initialize donations slider with specific configuration
        const donationsSlider = document.querySelector('.ul-donations-slider');
        if (donationsSlider) {
          // Destroy existing instance if it exists
          if (donationsSlider.swiper) {
            donationsSlider.swiper.destroy(true, true);
          }
          
          new window.Swiper('.ul-donations-slider', {
            slidesPerView: 6,
            spaceBetween: 20,
            autoplay: true,
            navigation: {
              prevEl: '.ul-donations-slider-nav .prev',
              nextEl: '.ul-donations-slider-nav .next',
            },
            breakpoints: {
              0: {
                slidesPerView: 1.2,
                centeredSlides: true,
              },
              480: {
                slidesPerView: 1.7,
                centeredSlides: true,
              },
              576: {
                slidesPerView: 2,
              },
              768: {
                slidesPerView: 3,
              },
              1200: {
                slidesPerView: 4,
                spaceBetween: 20,
              },
              1400: {
                slidesPerView: 4,
              },
              1680: {
                slidesPerView: 4,
                spaceBetween: 27,
              },
              1700: {
                slidesPerView: 4,
                spaceBetween: 30,
              }
            }
          });
        }

        // Initialize other swiper sliders (gallery, etc.)
        const otherSwipers = document.querySelectorAll('.swiper:not(.swiper-initialized):not(.ul-donations-slider)');
        otherSwipers.forEach(el => {
          if (el.swiper) {
            el.swiper.destroy(true, true);
          }
          new window.Swiper(el, {
            slidesPerView: 'auto',
            spaceBetween: 30,
            navigation: {
              nextEl: el.parentElement?.querySelector('.next'),
              prevEl: el.parentElement?.querySelector('.prev'),
            },
          });
        });
      }
    };

    // Wait for scripts to load, then initialize
    const checkAndInit = () => {
      if (window.Swiper && window.WOW) {
        initSliders();
        setTimeout(initProgressBars, 100);
      } else {
        setTimeout(checkAndInit, 100);
      }
    };

    checkAndInit();
  }, []);

  // Initialize accordion functionality
  useEffect(() => {
    const initAccordion = () => {
      const accordionContainers = document.querySelectorAll(".ul-accordion");
      
      accordionContainers.forEach(accordionContainer => {
        const accordionItems = accordionContainer.querySelectorAll(".ul-single-accordion-item");
        
        accordionItems.forEach((accordionItem) => {
          // Skip if already initialized
          if (accordionItem.hasAttribute('data-accordion-initialized')) {
            return;
          }
          
          accordionItem.setAttribute('data-accordion-initialized', 'true');
          
          const header = accordionItem.querySelector(".ul-single-accordion-item__header");
          if (header) {
            header.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              const openedItems = accordionContainer.querySelector(".ul-single-accordion-item.open");
              
              if (accordionItem.classList.contains("open")) {
                accordionItem.classList.remove("open");
              } else {
                accordionItem.classList.add("open");
                if (openedItems && openedItems !== accordionItem) {
                  openedItems.classList.remove("open");
                }
              }
            });
          }
        });
      });
    };

    // Initialize immediately and also after a short delay to ensure DOM is ready
    initAccordion();
    const timeoutId = setTimeout(initAccordion, 100);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Handle hash navigation to scroll to section
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (hash === '#get-involved') {
        setTimeout(() => {
          const element = document.getElementById('get-involved');
          if (element) {
            const headerOffset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    };

    // Handle on mount
    handleHashNavigation();

    // Handle hash change
    window.addEventListener('hashchange', handleHashNavigation);
    return () => window.removeEventListener('hashchange', handleHashNavigation);
  }, []);

  return (
    <>
      <Preloader />
      <Sidebar />
      <Header />
      
      {showAlert && (
        <AlertBar
          type={alert.type}
          message={alert.message}
          onClose={() => setShowAlert(false)}
        />
      )}

      <main className="overflow-hidden">

      <section className="ul-banner">
        <div className="ul-banner-container">
          <div
            className="row gy-4 row-cols-lg-2 row-cols-1 align-items-center flex-column-reverse flex-lg-row"
          >

            <div className="col">
              <div className="ul-banner-txt" style={{paddingTop: 'clamp(120px, 12vw, 200px)'}}>
                <div className="wow animate__fadeInUp">
                  <span className="ul-banner-sub-title ul-section-sub-title"
                    >STUDENT-LED NONPROFIT · SCIENCE-BASED</span
                  >
                  <h1 className="ul-banner-title">
                    Built by Students. Rooted in Community
                  </h1>
                  <p className="ul-banner-descr" style={{minHeight: 'clamp(50px, 9vw, 115px)', lineHeight: '1.8', marginBottom: 'clamp(20px, 2vw, 35px)'}}>
                    Pathio is a student-led 501(c)(3) nonprofit providing peer-to-peer guidance and mentorship for students exploring healthcare careers, grounded in shared experience and honest navigation.
                  </p>
                  <div className="ul-banner-btns" style={{paddingBottom: 'clamp(20px, 2vw, 40px)', marginBottom: 'clamp(10px, 1vw, 20px)'}}>
                    <Link to="/contact" className="ul-btn"
                      ><i
                        className="flaticon-fast-forward-double-right-arrows-symbol"
                      ></i>
                      Get Involved</Link
                    >

                    <div className="ul-banner-stat">
                      <div className="imgs">
                        <img
                          src="/assets/new-img/new/portrait-of-diverse-happy-schoolgirls-embracing-in-2025-04-04-11-29-35-utc.jpg"
                          alt="Person"
                        />
                        <img
                          src="/assets/new-img/new/happy-multiracial-senior-friends-drinking-a-tea-af-2024-12-05-16-09-34-utc.jpg"
                          alt="Person"
                        />
                        <img
                          src="/assets/new-img/new/group-of-happy-children-looking-at-camera-together-2025-03-08-01-50-52-utc.jpg"
                          alt="Person"
                        />
                        <span className="number">21K</span>
                      </div>
                      <span className="txt">People reached</span>
                    </div>
                  </div>
                </div>

                <img
                  src="/assets/img/banner-img-vector-1.png"
                  alt="Vector"
                  className="ul-banner-txt-vector"
                />
              </div>
            </div>

            <div className="col align-self-start">
              <div className="ul-banner-img">
                <div className="img-wrapper">
                  <img
                    src="/assets/new-img/happy-volunteers-putting-hands-together-wearing-gl-2025-10-05-19-19-30-utc.jpg"
                    alt="Banner Image"
                  />

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ul-about ul-section-spacing wow animate__fadeInUp">
        <div className="ul-container">
          <div
            className="row row-cols-md-2 row-cols-1 align-items-center gy-4 ul-about-row"
          >
            <div className="col">
              <div className="ul-about-imgs">
                <div className="img-wrapper">
                  <img
                    src="/assets/new-img/young-happy-volunteers-outdoor-meeting-at-park-2025-03-18-17-25-46-utc.jpg"
                    alt="Image"
                  />
                </div>
                <div className="ul-about-imgs-vectors">
                  <img
                    src="/assets/img/about-img-vector-1.svg"
                    alt="Image"
                    className="vector-1"
                  />
                  <img
                    src="/assets/img/about-img-vector-2.svg"
                    alt="Image"
                    className="vector-2"
                  />
                </div>
              </div>
            </div>

            <div className="col">
              <div className="ul-about-txt">
                <span className="ul-section-sub-title ul-section-sub-title--2"
                  >About PATHIO</span
                >
                <h2 className="ul-section-title">
                  A Student-Led Nonprofit Focused on Healthcare Pathways
                </h2>
                <p className="ul-section-descr">
                  Pathio is a student-led 501(c)(3) nonprofit providing peer-to-peer guidance and mentorship for students exploring healthcare careers, with a focus on Uzbek and Central Asian communities.
                  Through shared experiences, intentional mentorship, and community connection, we help students navigate complex academic and professional pathways
                </p>

                <div className="ul-about-block">
                  <div className="block-left">
                    <div className="block-heading">
                      <div className="icon"><i className="flaticon-love"></i></div>
                      <h3 className="block-title">Join Our Mission</h3>
                    </div>

                    <ul className="block-list">
                      <li>
                        Peer mentorship and student-to-student guidance
                      </li>
                      <li>Community conversations and storytelling</li>
                      <li>Resource navigation and referrals to trusted institutions</li>
                    </ul>
                  </div>
                  <div className="block-right">
                    <img
                      src="/assets/new-img/group-of-diversity-people-volunteen-arm-around-2025-02-10-01-27-31-utc.jpg"
                      alt="Image"
                    />
                  </div>
                </div>

                <div className="ul-about-bottom">
                  <Link to="/about" className="ul-btn"
                    ><i
                      className="flaticon-fast-forward-double-right-arrows-symbol"
                    ></i>
                    Learn More</Link
                  >

                  <div className="ul-about-call">
                    <div className="icon">
                      <i className="flaticon-email"></i>
                    </div>
                    <div className="txt">
                      <span className="call-title">Get Support</span>
                      <Link to="/contact">Contact Us</Link>
                    </div>
                  </div>
                </div>
              </div>
              <DonateModal open={donateModalOpen} amount={donateAmountSelected} onClose={() => setDonateModalOpen(false)} onProceed={({amount}) => {
                // Simulate payment success for frontend-only flow
                setDonateModalOpen(false);
                alert(`Thanks — simulated donation of $${amount} received.`);
              }} />
            </div>
          </div>
        </div>

        <div className="ul-about-vectors">
          <img
            src="/assets/img/about-vector-1.png"
            alt="vector"
            className="vector-1"
          />
        </div>
      </section>

      {/* <section className="ul-donations ul-section-spacing overflow-hidden">
        <div className="ul-container">
          <div
            className="ul-section-heading ul-donations-heading d-flex justify-content-between text-center"
          >
            <div className="left">
              <span className="ul-section-sub-title"
                ><span className="txt">Active Campaigns</span></span
              >
              <h2 className="ul-section-title">
                Health Literacy Campaigns Making Real Impact
              </h2>
            </div>

            <div className="flex-shrink-0">
              <div className="ul-banner-stat">
                <div className="imgs">
                  <img
                    src="/assets/new-img/new/portrait-of-diverse-happy-schoolgirls-embracing-in-2025-04-04-11-29-35-utc.jpg"
                    alt="Person"
                  />
                  <img
                    src="/assets/new-img/new/happy-multiracial-senior-friends-drinking-a-tea-af-2024-12-05-16-09-34-utc.jpg"
                    alt="Person"
                  />
                  <img
                    src="/assets/new-img/new/group-of-happy-children-looking-at-camera-together-2025-03-08-01-50-52-utc.jpg"
                    alt="Person"
                  />
                  <span className="number">2.M</span>
                </div>
                <span className="txt text-white">Active donors</span>
              </div>
            </div>
            <div className="ul-slider-nav ul-donations-slider-nav">
              <button className="prev"><i className="flaticon-back"></i></button>
              <button className="next"><i className="flaticon-next"></i></button>
            </div>
          </div>
        </div>

        <div className="ul-container wow animate__fadeInUp">
          <div className="ul-donations-slider swiper overflow-visible">
            <div className="swiper-wrapper">

              <div className="swiper-slide">
                <div className="ul-donation">
                  <div className="ul-donation-img">
                    <img
                      src="/assets/new-img/teacher-explaining-the-work-of-solar-panel-to-stud-2025-02-11-18-50-44-utc.jpg"
                      alt="donation Image"
                    />
                    <span className="tag">Health</span>
                  </div>
                  <div className="ul-donation-txt">
                    <div className="ul-donation-progress">
                      <div
                        className="donation-progress-container ul-progress-container"
                      >
                        <div
                          className="donation-progressbar ul-progressbar"
                          data-ul-progress-value="55"
                        >
                          <div
                            className="donation-progress-label ul-progress-label"
                          ></div>
                        </div>
                      </div>
                      <div className="ul-donation-progress-labels">
                        <span className="ul-donation-progress-label"
                          >People Reached : 2,100</span
                        >
                        <span className="ul-donation-progress-label"
                          >Target : 3,000</span
                        >
                      </div>
                    </div>
                    <Link to="/donations" className="ul-donation-title"
                      >Diabetes Education for Uzbek & Central Asian Families</Link
                    >
                    <p className="ul-donation-descr">
                      O'zbek va Markaziy Osiyo oilalari uchun ikki tilli
                      posterlar, TikTok tushuntiruvchilar va klinika resurs
                      kartochkalari. NYC tumanlaridagi tasdiqlangan tibbiy
                      yordam navigatorlariga ulanish.
                    </p>
                    <Link to="/donations" className="ul-donation-btn"
                      >Batafsil <i className="flaticon-up-right-arrow"></i
                    ></Link>
                  </div>
                </div>
              </div>

              <div className="swiper-slide">
                <div className="ul-donation">
                  <div className="ul-donation-img">
                    <img
                      src="/assets/new-img/presenting-renewable-sources-of-energy-2025-03-17-06-37-43-utc.jpg"
                      alt="donation Image"
                    />
                    <span className="tag">Health</span>
                  </div>
                  <div className="ul-donation-txt">
                    <div className="ul-donation-progress">
                      <div
                        className="donation-progress-container ul-progress-container"
                      >
                        <div
                          className="donation-progressbar ul-progressbar"
                          data-ul-progress-value="78"
                        >
                          <div
                            className="donation-progress-label ul-progress-label"
                          ></div>
                        </div>
                      </div>
                      <div className="ul-donation-progress-labels">
                        <span className="ul-donation-progress-label"
                          >People Reached : 3,200</span
                        >
                        <span className="ul-donation-progress-label"
                          >Target : 4,100</span
                        >
                      </div>
                    </div>
                    <Link to="/donations" className="ul-donation-title"
                      >Hypertension Management Guide - O'zbek/Russian</Link
                    >
                    <p className="ul-donation-descr">
                      Qon bosimi boshqaruvi bo'yicha vizual qo'llanmalar,
                      jamoaviy seminarlar va mahalliy klinikalarga resurs
                      xaritalari. Oilalarga qon bosimi tushunchasini va mahalliy
                      klinikalarni topishga yordam beradi.
                    </p>
                    <Link to="/donations" className="ul-donation-btn"
                      >Batafsil <i className="flaticon-up-right-arrow"></i
                    ></Link>
                  </div>
                </div>
              </div>

              <div className="swiper-slide">
                <div className="ul-donation">
                  <div className="ul-donation-img">
                    <img
                      src="/assets/new-img/high-school-students-raising-hands-in-classroom-du-2025-03-05-05-09-38-utc.jpg"
                      alt="donation Image"
                    />
                    <span className="tag">Health</span>
                  </div>
                  <div className="ul-donation-txt">
                    <div className="ul-donation-progress">
                      <div
                        className="donation-progress-container ul-progress-container"
                      >
                        <div
                          className="donation-progressbar ul-progressbar"
                          data-ul-progress-value="62"
                        >
                          <div
                            className="donation-progress-label ul-progress-label"
                          ></div>
                        </div>
                      </div>
                      <div className="ul-donation-progress-labels">
                        <span className="ul-donation-progress-label"
                          >People Reached : 1,850</span
                        >
                        <span className="ul-donation-progress-label"
                          >Target : 3,000</span
                        >
                      </div>
                    </div>
                    <Link to="/donations" className="ul-donation-title"
                      >NYC Clinic Directory - Uzbek/Russian/Tajik Languages</Link
                    >
                    <p className="ul-donation-descr">
                      Interaktiv xaritalar va QR-kodli kartochkalar. NYC
                      tumanlaridagi 162+ tasdiqlangan bepul va arzon
                      klinikalarga ulanish, til va xizmatlar bo'yicha
                      filtrlangan. O'zbek, Rus va Tojik tillarida.
                    </p>
                    <Link to="/donations" className="ul-donation-btn"
                      >Batafsil <i className="flaticon-up-right-arrow"></i
                    ></Link>
                  </div>
                </div>
              </div>

              <div className="swiper-slide">
                <div className="ul-donation">
                  <div className="ul-donation-img">
                    <img
                      src="/assets/new-img/young-diverse-people-having-fun-outdoor-laughing-t-2025-10-14-07-40-19-utc.jpg"
                      alt="donation Image"
                    />
                    <span className="tag">Health</span>
                  </div>
                  <div className="ul-donation-txt">
                    <div className="ul-donation-progress">
                      <div
                        className="donation-progress-container ul-progress-container"
                      >
                        <div
                          className="donation-progressbar ul-progressbar"
                          data-ul-progress-value="85"
                        >
                          <div
                            className="donation-progress-label ul-progress-label"
                          ></div>
                        </div>
                      </div>
                      <div className="ul-donation-progress-labels">
                        <span className="ul-donation-progress-label"
                          >Students Mentored : 87</span
                        >
                        <span className="ul-donation-progress-label"
                          >Target : 102</span
                        >
                      </div>
                    </div>
                    <Link to="/donations" className="ul-donation-title"
                      >Student Mentorship Program - Healthcare Career
                      Pathways</Link
                    >
                    <p className="ul-donation-descr">
                      Tibbiyot sohasidagi aspirantlarga mentorlik, ko'nikmalarni
                      rivojlantirish va real dunyo bo'yicha yo'riqnomalar.
                      Tibbiyot ta'limi, tadqiqot va kontent yaratish tajribasi.
                      Tibbiyot karerasiga yo'llar yaratish.
                    </p>
                    <Link to="/donations" className="ul-donation-btn"
                      >Batafsil <i className="flaticon-up-right-arrow"></i
                    ></Link>
                  </div>
                </div>
              </div>

              <div className="swiper-slide">
                <div className="ul-donation">
                  <div className="ul-donation-img">
                    <img
                      src="/assets/new-img/teacher-explaining-the-work-of-solar-panel-to-stud-2025-02-11-18-50-44-utc.jpg"
                      alt="donation Image"
                    />
                    <span className="tag">Health</span>
                  </div>
                  <div className="ul-donation-txt">
                    <div className="ul-donation-progress">
                      <div
                        className="donation-progress-container ul-progress-container"
                      >
                        <div
                          className="donation-progressbar ul-progressbar"
                          data-ul-progress-value="45"
                        >
                          <div
                            className="donation-progress-label ul-progress-label"
                          ></div>
                        </div>
                      </div>
                      <div className="ul-donation-progress-labels">
                        <span className="ul-donation-progress-label"
                          >People Reached : 1,200</span
                        >
                        <span className="ul-donation-progress-label"
                          >Target : 2,700</span
                        >
                      </div>
                    </div>
                    <Link to="/donations" className="ul-donation-title"
                      >Mental Health Resources - Multilingual Support</Link
                    >
                    <p className="ul-donation-descr">
                      Ruhiy salomatlik bo'yicha vizual tushuntiruvchilar,
                      jamoaviy seminarlar va mahalliy yordam xizmatlariga resurs
                      xaritalari. Oilalarga ruhiy salomatlikni tushunish va
                      mahalliy yordam xizmatlarini topishga yordam beradi.
                      O'zbek, Rus va Tojik tillarida.
                    </p>
                    <Link to="/donations" className="ul-donation-btn"
                      >Batafsil <i className="flaticon-up-right-arrow"></i
                    ></Link>
                  </div>
                </div>
              </div>

              <div className="swiper-slide">
                <div className="ul-donation">
                  <div className="ul-donation-img">
                    <img
                      src="/assets/new-img/presenting-renewable-sources-of-energy-2025-03-17-06-37-43-utc.jpg"
                      alt="donation Image"
                    />
                    <span className="tag">Health</span>
                  </div>
                  <div className="ul-donation-txt">
                    <div className="ul-donation-progress">
                      <div
                        className="donation-progress-container ul-progress-container"
                      >
                        <div
                          className="donation-progressbar ul-progressbar"
                          data-ul-progress-value="70"
                        >
                          <div
                            className="donation-progress-label ul-progress-label"
                          ></div>
                        </div>
                      </div>
                      <div className="ul-donation-progress-labels">
                        <span className="ul-donation-progress-label"
                          >People Reached : 2,100</span
                        >
                        <span className="ul-donation-progress-label"
                          >Target : 3,000</span
                        >
                      </div>
                    </div>
                    <Link to="/donations" className="ul-donation-title"
                      >Asthma Management for NYC Families - Central Asian
                      Communities</Link
                    >
                    <p className="ul-donation-descr">
                      Astma boshqaruvi bo'yicha qo'llanmalar, vizual
                      tushuntiruvchilar va mahalliy klinikalarga resurs
                      xaritalari. NYC oilalariga astma tushunchasini va mahalliy
                      klinikalarni topishga yordam beradi. O'zbek va Rus
                      tillarida.
                    </p>
                    <Link to="/donations" className="ul-donation-btn"
                      >Batafsil <i className="flaticon-up-right-arrow"></i
                    ></Link>
                  </div>
                </div>
              </div>

              <div className="swiper-slide">
                <div className="ul-donation">
                  <div className="ul-donation-img">
                    <img
                      src="/assets/new-img/high-school-students-raising-hands-in-classroom-du-2025-03-05-05-09-38-utc.jpg"
                      alt="donation Image"
                    />
                    <span className="tag">Health</span>
                  </div>
                  <div className="ul-donation-txt">
                    <div className="ul-donation-progress">
                      <div
                        className="donation-progress-container ul-progress-container"
                      >
                        <div
                          className="donation-progressbar ul-progressbar"
                          data-ul-progress-value="55"
                        >
                          <div
                            className="donation-progress-label ul-progress-label"
                          ></div>
                        </div>
                      </div>
                      <div className="ul-donation-progress-labels">
                        <span className="ul-donation-progress-label"
                          >Workshops Held : 12</span
                        >
                        <span className="ul-donation-progress-label"
                          >Target : 22</span
                        >
                      </div>
                    </div>
                    <Link to="/donations" className="ul-donation-title"
                      >Community Health Literacy Workshops - Brooklyn &
                      Queens</Link
                    >
                    <p className="ul-donation-descr">
                      Ikki tilli jamoaviy seminarlar - diabet, qon bosimi va
                      ruhiy salomatlik bo'yicha. Barcha jamoaviy a'zolar uchun
                      ochiq. Brooklyn va Queens jamoaviy markazlarida
                      o'tkaziladi. O'zbek, Rus va Tojik tillarida.
                    </p>
                    <Link to="/donations" className="ul-donation-btn"
                      >Batafsil <i className="flaticon-up-right-arrow"></i
                    ></Link>
                  </div>
                </div>
              </div>

              <div className="swiper-slide">
                <div className="ul-donation">
                  <div className="ul-donation-img">
                    <img
                      src="/assets/new-img/young-diverse-people-having-fun-outdoor-laughing-t-2025-10-14-07-40-19-utc.jpg"
                      alt="donation Image"
                    />
                    <span className="tag">Health</span>
                  </div>
                  <div className="ul-donation-txt">
                    <div className="ul-donation-progress">
                      <div
                        className="donation-progress-container ul-progress-container"
                      >
                        <div
                          className="donation-progressbar ul-progressbar"
                          data-ul-progress-value="90"
                        >
                          <div
                            className="donation-progress-label ul-progress-label"
                          ></div>
                        </div>
                      </div>
                      <div className="ul-donation-progress-labels">
                        <span className="ul-donation-progress-label"
                          >Resources Created : 248</span
                        >
                        <span className="ul-donation-progress-label"
                          >Target : 275</span
                        >
                      </div>
                    </div>
                    <Link to="/donations" className="ul-donation-title"
                      >Visual Health Explainers Library - Science-Based
                      Content</Link
                    >
                    <p className="ul-donation-descr">
                      248+ vizual tushuntiruvchilar - diabet, qon bosimi, astma
                      va ruhiy salomatlik bo'yicha. Ilmiy asoslangan, oson
                      tushuniladigan va vizual jihatdan jozibali kontent.
                      O'zbek, Rus va Tojik tillarida. Ijtimoiy tarmoqlar uchun
                      moslashtirilgan.
                    </p>
                    <Link to="/donations" className="ul-donation-btn"
                      >Batafsil <i className="flaticon-up-right-arrow"></i
                    ></Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="ul-dontations-slider-pagination d-none"></div>
          </div>
        </div>
      </section> */}

      <section className="ul-donate-form-section ul-section-spacing" style={{ overflow: 'hidden' }}>
        {/* Background decorative elements */}
        <div style={{
          position: 'absolute',
          top: '-15%',
          right: '-5%',
          width: 'clamp(300px, 35vw, 500px)',
          height: 'clamp(300px, 35vw, 500px)',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(43, 122, 143, 0.3) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '-20%',
          left: '-8%',
          width: 'clamp(250px, 28vw, 400px)',
          height: 'clamp(250px, 28vw, 400px)',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0
        }}></div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'clamp(400px, 50vw, 700px)',
          height: 'clamp(400px, 50vw, 700px)',
          borderRadius: '50%',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          pointerEvents: 'none',
          zIndex: 0
        }}></div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'clamp(550px, 65vw, 900px)',
          height: 'clamp(550px, 65vw, 900px)',
          borderRadius: '50%',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          pointerEvents: 'none',
          zIndex: 0
        }}></div>

        <div className="ul-container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row gx-5 gy-4 align-items-center">
            {/* Left Column - Donation Form */}
              <div className="col-lg-6 position-relative">
                <div style={{
                  background: 'var(--white)',
                  borderRadius: 'clamp(16px, 1.5vw, 24px)',
                  padding: 'clamp(28px, 3vw, 48px)',
                  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.15)',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <h3 style={{
                    fontSize: 'clamp(22px, 1.8vw, 30px)',
                    fontWeight: 700,
                    fontFamily: 'var(--font-quicksand)',
                    marginBottom: 'clamp(6px, 0.6vw, 10px)',
                    color: 'var(--ul-black)'
                  }}>
                    Support Our Work
                  </h3>
                  <p style={{
                    fontSize: 'clamp(13px, 0.9vw, 15px)',
                    color: '#777',
                    marginBottom: 'clamp(20px, 2vw, 30px)',
                    lineHeight: '1.5'
                  }}>
                    Choose an amount to get started
                  </p>

                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const amount = donateAmountSelected === 'custom'
                      ? Number(customDonateAmount)
                      : Number(donateAmountSelected);

                    if (!amount || amount <= 0) {
                      alert('Please select or enter a valid donation amount.');
                      return;
                    }

                    navigate('/donate', { state: { amount: amount.toString() } });
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(5, 1fr)',
                      gap: 'clamp(8px, 0.8vw, 12px)',
                      marginBottom: 'clamp(16px, 1.6vw, 24px)'
                    }}>
                      {[10, 20, 30, 40, 50].map((amount) => (
                        <button
                          key={amount}
                          type="button"
                          onClick={() => {
                            setDonateAmountSelected(amount);
                            setCustomDonateAmount('');
                          }}
                          style={{
                            padding: 'clamp(10px, 1vw, 14px) 0',
                            borderRadius: 'clamp(8px, 0.7vw, 12px)',
                            border: donateAmountSelected === amount ? '2px solid var(--ul-primary)' : '2px solid #e8e8e8',
                            background: donateAmountSelected === amount ? 'var(--ul-primary)' : 'var(--white)',
                            color: donateAmountSelected === amount ? 'var(--white)' : 'var(--ul-black)',
                            fontWeight: 700,
                            fontSize: 'clamp(15px, 1.1vw, 18px)',
                            fontFamily: 'var(--font-quicksand)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          ${amount}
                        </button>
                      ))}
                    </div>

                    <div style={{
                      display: 'flex',
                      gap: 'clamp(10px, 1vw, 14px)',
                      alignItems: 'stretch'
                    }}>
                      <div style={{ flex: 1 }}>
                        <input
                          type="number"
                          placeholder="Custom Amount"
                          value={customDonateAmount}
                          onChange={(event) => {
                            setCustomDonateAmount(event.target.value);
                            setDonateAmountSelected('custom');
                          }}
                          onFocus={() => setDonateAmountSelected('custom')}
                          style={{
                            width: '100%',
                            padding: 'clamp(12px, 1.1vw, 16px) clamp(14px, 1.2vw, 20px)',
                            borderRadius: 'clamp(8px, 0.7vw, 12px)',
                            border: donateAmountSelected === 'custom' ? '2px solid var(--ul-primary)' : '2px solid #e8e8e8',
                            fontSize: 'clamp(14px, 1vw, 16px)',
                            fontFamily: 'inherit',
                            outline: 'none',
                            transition: 'all 0.2s ease',
                            boxSizing: 'border-box',
                            height: '100%'
                          }}
                        />
                      </div>
                      <button type="submit" className="ul-btn" style={{
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        borderRadius: 'clamp(8px, 0.7vw, 12px)',
                        height: '100%',
                        minHeight: 'clamp(44px, 3.2vw, 52px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <i className="flaticon-fast-forward-double-right-arrows-symbol"></i>
                        Support Now
                      </button>
                    </div>
                  </form>
                </div>
              </div>

            {/* Right Column - Campaign Info */}
            <div className="col-lg-6">
              <div style={{
                paddingLeft: 'clamp(20px, 2.5vw, 50px)',
                paddingTop: 'clamp(30px, 3vw, 50px)',
                paddingBottom: 'clamp(30px, 3vw, 50px)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
                gap: 'clamp(28px, 3vw, 40px)'
              }}>
                <div>
                  <h2 style={{
                    fontSize: 'clamp(28px, 2.6vw, 42px)',
                    lineHeight: '1.2',
                    marginBottom: 'clamp(12px, 1vw, 16px)',
                    fontWeight: 700,
                    letterSpacing: '-0.5px',
                    color: 'var(--white)'
                  }}>
                    Support Our Mission
                  </h2>
                  <p style={{
                    fontSize: 'clamp(15px, 1.05vw, 17px)',
                    lineHeight: '1.7',
                    color: 'rgba(255, 255, 255, 0.75)',
                    margin: 0
                  }}>
                    Every dollar helps us build mentorship programs and resources for students navigating healthcare careers.
                  </p>
                </div>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'clamp(16px, 1.5vw, 22px)'
                }}>
                  {[
                    { icon: 'flaticon-right-arrow', label: 'Pathway Guides' },
                    { icon: 'flaticon-team', label: 'Peer Mentorship' },
                    { icon: 'flaticon-relationship', label: 'Community Programs' }
                  ].map((item, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'clamp(14px, 1.2vw, 18px)'
                    }}>
                      <div style={{
                        width: 'clamp(40px, 3vw, 48px)',
                        height: 'clamp(40px, 3vw, 48px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: '50%',
                        border: '1px solid rgba(255, 255, 255, 0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <i className={item.icon} style={{
                          fontSize: 'clamp(17px, 1.3vw, 21px)',
                          color: 'var(--white)'
                        }}></i>
                      </div>
                      <span style={{
                        fontSize: 'clamp(15px, 1.1vw, 17px)',
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: 600
                      }}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>

                <p style={{
                  fontSize: 'clamp(12px, 0.85vw, 14px)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  margin: 0,
                  lineHeight: '1.5'
                }}>
                  Pathio is a 501(c)(3) nonprofit. Donations are tax-deductible.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="get-involved" className="ul-why-join ul-section-spacing">
        <div className="ul-why-join-wrapper ul-section-spacing">
          <div className="ul-container">
            <div className="row row-cols-md-2 row-cols-1 gy-4 align-items-center">
              <div className="col">
                <div className="ul-why-join-img">
                  <img
                    src="/assets/new-img/happy-volunteers-putting-hands-together-wearing-gl-2025-10-05-19-19-30-utc.jpg"
                    alt="Image"
                  />
                </div>
              </div>

              <div className="col">
                <div className="ul-why-join-txt">
                  <span className="ul-section-sub-title">Join us</span>
                  <h2 className="ul-section-title">
                    Why We Need You: Mentors, Creators & Partners
                  </h2>
                  <p className="ul-section-descr">
                    Whether you're a healthcare mentor, student creative,
                    community member, school partner, or funder—Pathio has a
                    path for you to make a real impact.
                  </p>

                  <div className="ul-accordion">
                    <div className="ul-single-accordion-item open">
                      <div className="ul-single-accordion-item__header">
                        <div className="left">
                          <h3 className="ul-single-accordion-item__title">
                            Healthcare Professionals
                          </h3>
                        </div>
                        <span className="icon"><i className="flaticon-next"></i></span>
                      </div>

                      <div className="ul-single-accordion-item__body">
                        <p>
                          Share professional perspective, career insight, and lived experience to support Pathio's student-led mentorship, awareness efforts, and navigation initiatives.
                        </p>
                      </div>
                    </div>

                    <div className="ul-single-accordion-item">
                      <div className="ul-single-accordion-item__header">
                        <div className="left">
                          <h3 className="ul-single-accordion-item__title">
                            Community Partners & Organizations
                          </h3>
                        </div>
                        <span className="icon"><i className="flaticon-next"></i></span>
                      </div>

                      <div className="ul-single-accordion-item__body">
                        <p>
                          Collaborate with Pathio on outreach, programming, or resource connection efforts that support students and communities navigating healthcare pathways and access to care.
                        </p>
                      </div>
                    </div>

                    <div className="ul-single-accordion-item">
                      <div className="ul-single-accordion-item__header">
                        <div className="left">
                          <h3 className="ul-single-accordion-item__title">
                            Content & Media Contributors
                          </h3>
                        </div>
                        <span className="icon"><i className="flaticon-next"></i></span>
                      </div>

                      <div className="ul-single-accordion-item__body">
                        <p>
                          Support storytelling, design, and communications that highlight student experiences, healthcare journeys, and community awareness initiatives.
                        </p>
                      </div>
                    </div>

                    <div className="ul-single-accordion-item">
                      <div className="ul-single-accordion-item__header">
                        <div className="left">
                          <h3 className="ul-single-accordion-item__title">
                            Volunteers
                          </h3>
                  </div>
                        <span className="icon"><i className="flaticon-next"></i></span>
                </div>

                      <div className="ul-single-accordion-item__body">
                        <p>
                          Assist with events, outreach, operations, or behind-the-scenes support that strengthens Pathio's mentorship, navigation, and awareness-focused work.
                        </p>
              </div>
            </div>

                    <div className="ul-single-accordion-item">
                      <div className="ul-single-accordion-item__header">
                        <div className="left">
                          <h3 className="ul-single-accordion-item__title">
                            Supporters & Donors
                          </h3>
                        </div>
                        <span className="icon"><i className="flaticon-next"></i></span>
                      </div>

                      <div className="ul-single-accordion-item__body">
                        <p>
                          Help sustain Pathio's student-led programs, community initiatives, and long-term impact by supporting our mission financially or strategically.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ul-team ul-section-spacing pt-0">
        <div className="ul-container">

          <div className="ul-section-heading justify-content-between">
            <div className="left">
              <span className="ul-section-sub-title">Our Team</span>
              <h2 className="ul-section-title">
                Meet Our Student Leaders
              </h2>
            </div>
            <div>
              <Link to="/team" className="ul-btn"
                ><i
                  className="flaticon-fast-forward-double-right-arrows-symbol"
                ></i>
                Get Involved</Link
              >
            </div>
          </div>

          <div
            className="row row-cols-md-4 row-cols-sm-3 row-cols-2 row-cols-xxs-1 ul-team-row justify-content-center"
          >

            <div className="col">
              <div className="ul-team-member ul-team-member-no-bio">
                <div className="ul-team-member-front">
                <div className="ul-team-member-img">
                  <img
                      src="/assets/team/doston.JPG"
                      alt="Doston - Team Member"
                  />
                </div>
                <div className="ul-team-member-info">
                  <h3 className="ul-team-member-name">
                      <Link to="/team">Doston Karimov</Link>
                  </h3>
                  <p className="ul-team-member-designation">Founder and President</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col">
              <div className="ul-team-member ul-team-member-no-bio">
                <div className="ul-team-member-front">
                <div className="ul-team-member-img">
                  <img
                      src="/assets/team/zeeshan.JPG"
                      alt="Zeeshan - Team Member"
                  />
                  <div className="ul-team-member-socials">
                      <a href="https://www.linkedin.com/in/zeeshan-gondal/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><i className="flaticon-linkedin-big-logo"></i></a>
                  </div>
                </div>
                <div className="ul-team-member-info">
                  <h3 className="ul-team-member-name">
                      <Link to="/team">Zeeshan Gondal</Link>
                  </h3>
                  <p className="ul-team-member-designation">Vice President</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col">
              <div className="ul-team-member ul-team-member-no-bio">
                <div className="ul-team-member-front">
                <div className="ul-team-member-img">
                  <img
                      src="/assets/team/yulduz.JPG"
                      alt="Yulduz - Team Member"
                  />
                </div>
                <div className="ul-team-member-info">
                  <h3 className="ul-team-member-name">
                      <Link to="/team">Yulduz Mardonova</Link>
                  </h3>
                  <p className="ul-team-member-designation">Treasurer</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col">
              <div className="ul-team-member ul-team-member-no-bio">
                <div className="ul-team-member-front">
                <div className="ul-team-member-img">
                  <img
                      src="/assets/team/marjona.JPG"
                      alt="Marjona - Team Member"
                  />
                </div>
                <div className="ul-team-member-info">
                  <h3 className="ul-team-member-name">
                      <Link to="/team">Marjona Burhanova</Link>
                  </h3>
                  <p className="ul-team-member-designation">Secretary</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <Testimonials /> */}

   

    </main>

      <Footer />
    </>
  );
};

export default Index;
